import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { ChangeRequestService } from '../../../service/api/change-request-service';

const { CHANGEREQUEST_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'changeRequest';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  fetchLoading: false,
  saveLoading: false,
  error: null,
  filters: {
    searchText: '',
    status: null,
  },
  filterVisible: false,
  tileView: true,
};

export const fetchChangeRequests = createAsyncThunk(
  'changeRequest/fetchChangeRequests',
  async (_, { rejectWithValue }) => {
    try {
      return await ChangeRequestService.getAllChangeRequests();
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load change requests');
    }
  },
);

export const fetchChangeRequestById = createAsyncThunk(
  'changeRequest/fetchChangeRequestById',
  async (id, { rejectWithValue }) => {
    try {
      const detail = await ChangeRequestService.getChangeRequestById(id);
      if (!detail) {
        return rejectWithValue('Change request not found');
      }
      return detail;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load change request');
    }
  },
);

export const addChangeRequest = createAsyncThunk(
  'changeRequest/addChangeRequest',
  async (payload, { rejectWithValue }) => {
    try {
      const toSave = { ...payload, id: 0, changeRequestId: 0 };
      await ChangeRequestService.saveChangeRequest(toSave);
      return true;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add change request');
    }
  },
);

export const editChangeRequest = createAsyncThunk(
  'changeRequest/editChangeRequest',
  async (payload, { rejectWithValue }) => {
    try {
      await ChangeRequestService.saveChangeRequest(payload);
      return true;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update change request');
    }
  },
);

export const deleteChangeRequest = createAsyncThunk(
  'changeRequest/deleteChangeRequest',
  async (id, { rejectWithValue }) => {
    try {
      // API delete not implemented — local list only until backend exists
      return id;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete change request');
    }
  },
);

export const { reducer, actions } = createSlice({
  name: STORE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchChangeRequests.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChangeRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchChangeRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchChangeRequestById.pending, state => {
        state.fetchLoading = true;
      })
      .addCase(fetchChangeRequestById.fulfilled, state => {
        state.fetchLoading = false;
      })
      .addCase(fetchChangeRequestById.rejected, state => {
        state.fetchLoading = false;
      })
      .addCase(addChangeRequest.pending, state => {
        state.saveLoading = true;
        state.error = null;
      })
      .addCase(addChangeRequest.fulfilled, state => {
        state.saveLoading = false;
        state.error = null;
      })
      .addCase(addChangeRequest.rejected, (state, action) => {
        state.saveLoading = false;
        state.error = action.payload;
      })
      .addCase(editChangeRequest.pending, state => {
        state.saveLoading = true;
        state.error = null;
      })
      .addCase(editChangeRequest.fulfilled, state => {
        state.saveLoading = false;
        state.error = null;
      })
      .addCase(editChangeRequest.rejected, (state, action) => {
        state.saveLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteChangeRequest.fulfilled, (state, action) => {
        state.data = state.data.filter(cr => cr.id !== action.payload);
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addChangeRequest.rejected, editChangeRequest.rejected),
  effect: (action, api) => {
    api.dispatch(showToast({ message: action.payload, type: 'danger' }));
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addChangeRequest.fulfilled, editChangeRequest.fulfilled),
  effect: (_, api) => {
    api.dispatch(closeModal(CHANGEREQUEST_ADD_EDIT));
    api.dispatch(showToast({ message: 'Change request saved successfully', type: 'success' }));
    api.dispatch(fetchChangeRequests());
  },
});

redux.listener.startListening({
  matcher: isAnyOf(fetchChangeRequestById.fulfilled),
  effect: (action, api) => {
    api.dispatch(showModal(CHANGEREQUEST_ADD_EDIT, action.payload));
  },
});

redux.listener.startListening({
  matcher: isAnyOf(fetchChangeRequests.rejected, fetchChangeRequestById.rejected),
  effect: (action, api) => {
    const msg = action.payload || 'Request failed';
    api.dispatch(showToast({ message: msg, type: 'danger' }));
  },
});

redux.listener.startListening({
  matcher: isAnyOf(deleteChangeRequest.fulfilled),
  effect: (_, api) => {
    api.dispatch(showToast({ message: 'Change request deleted successfully', type: 'success' }));
  },
});

redux.injectReducer(STORE_KEY, reducer);
